import { useEffect, useState } from 'react';
import { Box, Typography } from '@coolblue-development/freddy';
import { PlayCircled } from '@coolblue-development/icons';
import { useEmailContext } from '../../../../contexts/EmailContext';
import { formatDuration } from '../../../../utils/formatDuration';
import PauseIcon from '../../Icons/PauseIcon';
import Label from '../../Label';

interface EmailViewTimerProps {
  contactId: string;
}

const ICON_COLOR = '#666666';

// Total time the agent has spent on this email. Unlike the Connect-based Timer, it pauses
// while the agent is on another channel, so it reads the accumulated value on each tick.
// The play/pause icon only indicates whether the timer is counting right now; it isn't a control.

export const EmailViewTimer = ({ contactId }: EmailViewTimerProps): JSX.Element => {
  const { emailViewTime } = useEmailContext();
  const { getViewMs, isRunning } = emailViewTime;
  const [duration, setDuration] = useState(() => getViewMs(contactId));
  const running = isRunning(contactId);

  useEffect(() => {
    setDuration(getViewMs(contactId));
    const timerId = setInterval(() => setDuration(getViewMs(contactId)), 1000);
    return () => clearInterval(timerId);
  }, [contactId, getViewMs, running]);

  return (
    <Label data-testid="email-view-timer" color="grey" variant="subtle">
      <Box
        data-testid="email-view-timer-state"
        role="img"
        aria-label={running ? 'Timer running' : 'Timer paused'}
        sx={{ display: 'flex' }}
      >
        {running ? <PlayCircled fill={ICON_COLOR} size={16} /> : <PauseIcon color={ICON_COLOR} />}
      </Box>
      <Typography variant="body" sx={{ marginLeft: '5px' }}>
        {formatDuration(duration)}
      </Typography>
    </Label>
  );
};
